import { supabase } from '../lib/supabase';
import axios from 'axios'; // Adicionando axios para a chamada do webhook

export type AgentType = 'bulk_send' | 'whatsapp_agent';

export interface AgentConfig {
  id?: string;
  agent_type: AgentType;
  model_config: any;
  prompt_config: any;
  full_prompt?: string; // Novo campo para armazenar o prompt completo
}

const WHATSAPP_AGENT_WEBHOOK_URL = 'https://webhook.solvion.com.br/webhook/ia_mi_buscador';
const BULK_SEND_FULL_PROMPT_WEBHOOK_URL = 'https://webhook.solvion.com.br/webhook/mi-buscador-bulk-full-prompt';

/**
 * Salva ou atualiza a configuração de um agente de IA para o usuário autenticado.
 */
export async function saveAgentConfig(config: AgentConfig): Promise<AgentConfig> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    throw new Error('Usuário não autenticado.');
  }

  const dataToUpsert = {
    user_id: user.id,
    agent_type: config.agent_type,
    model_config: config.model_config,
    prompt_config: config.prompt_config,
    full_prompt: config.full_prompt, // Incluindo o novo campo (pode ser undefined inicialmente)
  };

  // 1. Salvar no Supabase
  const { data, error } = await supabase
    .from('ai_agent_configs')
    .upsert(
      dataToUpsert,
      {
        onConflict: 'user_id, agent_type',
        ignoreDuplicates: false,
      }
    )
    .select()
    .single();

  if (error) {
    console.error(`Error saving ${config.agent_type} config:`, error);
    throw new Error(`Falha ao salvar a configuração do agente ${config.agent_type}.`);
  }

  const savedConfig = data as AgentConfig;

  // 2. Chamar o Webhook Externo
  const webhookPayload = {
    user_id: user.id,
    agent_type: config.agent_type,
    model_config: config.model_config,
    prompt_config: config.prompt_config,
    config_id: savedConfig.id,
  };

  let targetUrl: string | null = null;

  if (config.agent_type === 'whatsapp_agent') {
    targetUrl = WHATSAPP_AGENT_WEBHOOK_URL;
  } else if (config.agent_type === 'bulk_send') {
    targetUrl = BULK_SEND_FULL_PROMPT_WEBHOOK_URL;
  }

  if (targetUrl) {
    try {
      // Chamada não bloqueante (fire-and-forget)
      await axios.post(targetUrl, webhookPayload, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 10000 // 10 segundos de timeout
      });
    } catch {
      // Webhook falhou silenciosamente em produção
    }
  }

  return savedConfig;
}

/**
 * Busca a configuração de um agente de IA específica.
 */
export async function fetchAgentConfig(agentType: AgentType): Promise<AgentConfig | null> {
  const { data, error } = await supabase
    .from('ai_agent_configs')
    .select('id, agent_type, model_config, prompt_config, full_prompt')
    .eq('agent_type', agentType)
    .single();

  if (error && error.code !== 'PGRST116') {
    throw new Error(`Falha ao carregar a configuração do agente ${agentType}.`);
  }

  return data ? (data as AgentConfig) : null;
}


export interface WhatsAppInstance {
  id: string;
  instance_name: string;
  instance_id: string;
  status: string;
}

export interface AgentActivationPayload {
  instance_name: string;
  instance_id: string;
  user_email: string;
  subscription_plan: string;
  agent_status: 'active' | 'inactive';
  timestamp: string;
}

const AGENT_ACTIVATION_WEBHOOK_URL = 'https://webhook.solvion.com.br/webhook/ativar-agente';

/**
 * Busca a instância WhatsApp do usuário autenticado
 */
export async function getWhatsAppInstance(): Promise<WhatsAppInstance | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    return null;
  }

  const { data, error } = await supabase
    .from('user_credentials')
    .select('id, api_key, instance_details, is_active')
    .eq('user_id', user.id)
    .eq('integration_name', 'whatsapp')
    .single();

  if (error && error.code !== 'PGRST116') {
    return null;
  }

  if (!data) {
    return null;
  }

  // Extrair informações do instance_details JSON
  const instanceDetails = data.instance_details as any;

  return {
    id: data.id,
    instance_name: data.api_key, // api_key contém o nome da instância
    instance_id: instanceDetails?.uniqueName || data.api_key,
    status: instanceDetails?.status || (data.is_active ? 'CONNECTED' : 'DISCONNECTED')
  } as WhatsAppInstance;
}

/**
 * Ativa ou desativa o agente de atendimento WhatsApp
 */
export async function toggleWhatsappAgent(
  isActive: boolean,
  instanceName: string,
  instanceId: string,
  userEmail: string,
  subscriptionPlan: string
): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    throw new Error('Usuário não autenticado.');
  }

  const payload: AgentActivationPayload = {
    instance_name: instanceName,
    instance_id: instanceId,
    user_email: userEmail,
    subscription_plan: subscriptionPlan,
    agent_status: isActive ? 'active' : 'inactive',
    timestamp: new Date().toISOString(),
  };

  try {
    await axios.post(AGENT_ACTIVATION_WEBHOOK_URL, payload, {
      headers: { 'Content-Type': 'application/json' },
      timeout: 15000,
    });
  } catch {
    throw new Error('Falha ao comunicar com o servidor. Tente novamente.');
  }

  const { error } = await supabase
    .from('ai_agent_configs')
    .update({ is_active: isActive })
    .eq('user_id', user.id)
    .eq('agent_type', 'whatsapp_agent');

  if (error) {
    throw new Error('Falha ao salvar o status do agente.');
  }
}